import React, { useState, useEffect } from "react";
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import { Button } from "../PSY/Button"

import {
  Decimal,
  Decimalish,
  StabilityDeposit,
  LiquityStoreState,
  StabilityDepositChange
} from "@liquity/lib-base";

import { useLiquitySelector } from "@liquity/lib-react";

import { COIN } from "../../strings";

import { LoadingOverlay } from "../LoadingOverlay";
import { InputField } from "../PSY/InputField";
import { StabilityDepositAction } from "./StabilityDepositAction";
import { SLSD } from "../../lexicon";
import { ActionDescription } from "../ActionDescription";

const tabMode = {
  Deposit: 0,
  Withdraw: 1
} as const
type TabMode = typeof tabMode[keyof typeof tabMode]

const select = ({ slsdBalance }: LiquityStoreState) => ({
  slsdBalance,
});

type StabilityDepositEditorProps = {
  originalDeposit: StabilityDeposit;
  editedSLSD: Decimal;
  changePending: boolean;
  dispatch: (action: { type: "setDeposit"; newValue: Decimalish } | { type: "revert" }) => void;
  description?: JSX.Element
  validChange: StabilityDepositChange<Decimal> | undefined
  transactionId: string
  spAddress: string | undefined
};

export const StabilityDepositEditor: React.FC<StabilityDepositEditorProps> = ({
  originalDeposit,
  editedSLSD,
  changePending,
  dispatch,
  description,
  validChange,
  transactionId,
  spAddress,
}) => {
  const { slsdBalance } = useLiquitySelector(select);

  const [tab, setTab] = useState<TabMode>(tabMode.Deposit)

  // const maxAmount = originalDeposit.currentSLSD.add(slsdBalance);
  const maxAmount = slsdBalance;
  const maxedOut = editedSLSD.eq(maxAmount);

  // NOTE: for pool share
  // const slsdInStabilityPoolAfterChange = slsdInStabilityPool
  //   .sub(originalDeposit.currentSLSD)
  //   .add(editedSLSD);

  // const originalPoolShare = originalDeposit.currentSLSD.mulDiv(100, slsdInStabilityPool);
  // const newPoolShare = editedSLSD.mulDiv(100, slsdInStabilityPoolAfterChange);
  // const poolShareChange =
  //   originalDeposit.currentSLSD.nonZero &&
  //   Difference.between(newPoolShare, originalPoolShare).nonZero;


  const [depositSLSD, setDepositSLSD] = useState<Decimal>(Decimal.ZERO);
  const [withdrawSLSD, setWithdrawSLSD] = useState<Decimal>(Decimal.ZERO);

  const descElem = description ??
    (<ActionDescription>Enter the amount of {COIN} you'd like to {tab === tabMode.Deposit ? 'deposit' : 'withdraw'}.</ActionDescription>
    )

  useEffect(() => {
    if (!depositSLSD.isZero) {
      const newValue = originalDeposit.currentSLSD.add(depositSLSD)
      dispatch({ type: "setDeposit", newValue })
    }
  }, [depositSLSD])

    useEffect(() => {
    // TODO: withdrawSLSD is less than currentSLSD
    if (!withdrawSLSD.isZero) {
      const newValue = originalDeposit.currentSLSD.sub(withdrawSLSD)
      dispatch({ type: "setDeposit", newValue })
    }
  }, [withdrawSLSD])

  return (
    <Tabs selectedTabClassName="btn-tab--active" onSelect={(index) => setTab(index as TabMode)}>
      <TabList className="flex flex-row justify-between hover:cursor-pointer">
        <Tab className="btn-tab"><span>Deposit</span></Tab>
        <Tab className="btn-tab ml-3"><span>Withdraw</span></Tab>
      </TabList>
      <TabPanel>
        <InputField
          label="Deposit"
          amount={depositSLSD.toString(2)}
          maxAmount={maxAmount.toString()}
          usdValue={depositSLSD.mul(1).toString(2)}
          balance={slsdBalance.toString(2)}
          unit={COIN}
          icon={SLSD.iconUrl}
          setEditedAmount={newValue => setDepositSLSD(Decimal.from(newValue))}
        />

      </TabPanel>
      <TabPanel>
        {/* TODO-PSY: calculate usdValue*/}
        <InputField
          label="Withdraw"
          amount={withdrawSLSD.toString(2)}
          usdValue={withdrawSLSD.mul(1).prettify(2)}
          unit={COIN}
          icon={SLSD.iconUrl}
          setEditedAmount={newValue => {
            const newWithdrawSLSD = Decimal.from(newValue)
            if (newWithdrawSLSD.lte(originalDeposit.currentSLSD)) {
              setWithdrawSLSD(newWithdrawSLSD)
            }
          }}
        />

      </TabPanel>
      {descElem}
      <div className="flex flex-row mt-4">
        {tab === tabMode.Deposit && (
          !!validChange?.depositSLSD && !!spAddress ? (
            <StabilityDepositAction spAddress={spAddress} transactionId={transactionId} change={validChange}>
              Deposit
            </StabilityDepositAction>
          ) : (
            <Button disabled className="flex flex-1">Deposit</Button>
          )
        )}
        {tab === tabMode.Withdraw && (
          !!validChange?.withdrawSLSD && !!spAddress ? (
            <StabilityDepositAction spAddress={spAddress} transactionId={transactionId} change={validChange}>
              Withdraw
            </StabilityDepositAction>
          ) : (
            <Button disabled className="flex flex-1">Withdraw</Button>
          )
        )}
        <div className="spacer flex flex-1"></div>
      </div>

      {changePending && <LoadingOverlay />}
    </Tabs>
  );
};
